import { createSelector } from '@reduxjs/toolkit';
import { singleUserVersion } from '../organization/singleUserVersion';
import { currentEmployeeUuid } from '../employees/current-employee-uuid';
import { employeesForStaffingCalendar } from '../staffing/employees-for-staffing-calendar';
import type { ListItemIcon } from '../../../library/lists/listItemIcon';
import type { ListSectionItemInput } from '../../../library/lists/listSectionItemInput';
import {
  currentUserHasStaffingCalendarAccess,
  type EmployeeForStaffingCalendar,
  getOrganizationId,
  leavePeriodTypes,
} from 'src/store/selectors';
import { LocalDate } from 'src/utilities/local-date';
import { pageIds } from 'src/store/selectors/pages/pageIds';
import { orderBy } from 'lodash';
import type { LeavePeriod } from '@trinnvis/dabih-api-client/src/models/LeavePeriod';
import { today } from './today';

/**
 * Hvor langt tilbake i tid vi viser ubekreftet fravær på Overblikk-widgeten.
 * Sykefravær og lignende blir av og til registrert i etterkant, så et hardt
 * "fra og med i dag"-filter ville skjule legitime nye registreringer av
 * fravær fra forrige uke. 30 dager er kompromisset mellom å fange opp slike
 * etterregistreringer og å la widget-en bli en søppelkasse for gamle
 * perioder som aldri ble bekreftet.
 */
const UNCONFIRMED_LEAVE_LOOKBACK_DAYS = 30;

export const unconfirmedLeavePeriods = createSelector(
  singleUserVersion,
  currentUserHasStaffingCalendarAccess,
  employeesForStaffingCalendar,
  currentEmployeeUuid,
  getOrganizationId,
  today,
  (
    singleUser,
    staffingAccess,
    employees,
    currentEmployeeUuid,
    organizationId,
    todayString,
  ): ListSectionItemInput[] | undefined => {
    if (singleUser || !staffingAccess) {
      return undefined;
    }
    const cutoff = LocalDate.fromString(todayString).minusDays(UNCONFIRMED_LEAVE_LOOKBACK_DAYS);
    const periods: { employee: EmployeeForStaffingCalendar; period: LeavePeriod }[] = [];
    for (const employee of employees) {
      employee.leavePeriods.forEach((period) => {
        if (!period.confirmed && LocalDate.fromString(period.end).isSameOrAfter(cutoff)) {
          periods.push({
            employee: employee,
            period: period,
          });
        }
      });
    }
    const sorted = orderBy(periods, [(p) => p.period.start], ['desc']);
    return sorted.map((p) => {
      let employeeName = p.employee.displayName;
      if (p.employee.uuid === currentEmployeeUuid) {
        employeeName = 'Jeg';
      }
      let dates = LocalDate.fromString(p.period.start).toStringForDisplay();
      if (p.period.end !== p.period.start) {
        dates += ' - ' + LocalDate.fromString(p.period.end).toStringForDisplay();
      }
      const text = leavePeriodTypes.find((l) => l.value === p.period.type)?.text + ' ' + employeeName + ' ' + dates;
      return {
        mainIcon: p.period.type as ListItemIcon,
        unconfirmed: true,
        accessible: true,
        href: '/account/' + organizationId + '/' + pageIds.staffing,
        clickData: JSON.stringify({ employee: p.employee, period: p.period }),
        label: text,
      };
    });
  },
);
